/**
 * Overlay dialogs for the mall map: unit comparison and the lease abstract.
 * Both are presentational — the page owns which one is open.
 */

import React, { useMemo } from 'react';
import { X, FileText, GitCompare, Printer } from 'lucide-react';
import { formatMoney, formatMoneyExact, formatNumber } from '../../Shared/Formatters';
import { UNITS, formatDateLong, CATEGORY_COLORS } from '../data/mallData';

function Shell({ title, icon, onClose, children, wide }) {
    return (
        <div className="mm-modal-backdrop" onClick={onClose} role="presentation">
            <div
                className={`mm-modal${wide ? ' is-wide' : ''}`}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={title}
            >
                <header className="mm-modal-head">
                    <h3>{icon}{title}</h3>
                    <button type="button" onClick={onClose} aria-label="Close"><X size={18} /></button>
                </header>
                <div className="mm-modal-body">{children}</div>
            </div>
        </div>
    );
}

/**
 * Similar units = same category, within ±40% GLA, trading. Falls back to the
 * whole category when the size band is too thin to be useful.
 */
export function CompareModal({ unit, onClose, onSelectUnit }) {
    const peers = useMemo(() => {
        if (!unit) return [];
        const sameCat = UNITS.filter((u) => u.id !== unit.id && u.category === unit.category && u.performance);
        const band = sameCat.filter((u) => Math.abs(u.gla - unit.gla) / unit.gla <= 0.4);
        const pool = band.length >= 3 ? band : sameCat;
        return pool
            .sort((a, b) => Math.abs(a.gla - unit.gla) - Math.abs(b.gla - unit.gla))
            .slice(0, 6);
    }, [unit]);

    if (!unit) return null;
    const rows = [unit, ...peers];

    const metric = (u, fn, fallback = '—') => (u.performance ? fn(u.performance) : fallback);

    return (
        <Shell title={`Compare — ${unit.name}`} icon={<GitCompare size={16} />} onClose={onClose} wide>
            {peers.length === 0 ? (
                <p className="mm-note">No comparable trading units in {unit.category} on this plan.</p>
            ) : (
                <div className="mm-table-wrap">
                    <table className="mm-table">
                        <thead>
                            <tr>
                                <th>Unit</th>
                                <th>Floor</th>
                                <th className="num">GLA</th>
                                <th className="num">Rent / m²</th>
                                <th className="num">Sales / m²</th>
                                <th className="num">Sales 12m</th>
                                <th className="num">Occ. cost</th>
                                <th className="num">Conv.</th>
                                <th className="num">Risk</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((u) => (
                                <tr key={u.id} className={u.id === unit.id ? 'is-subject' : ''}>
                                    <td>
                                        <button type="button" className="mm-linkcell" onClick={() => onSelectUnit(u.id)}>
                                            <span className="mm-cat-dot" style={{ background: CATEGORY_COLORS[u.category] }} />
                                            <span>{u.name}</span>
                                            <em>{u.code}</em>
                                        </button>
                                    </td>
                                    <td>{u.floor}</td>
                                    <td className="num">{formatNumber(u.gla)} m²</td>
                                    <td className="num">{u.lease ? formatMoneyExact(u.lease.rentPerSqm) : '—'}</td>
                                    <td className="num">{metric(u, (p) => formatMoneyExact(p.salesPerSqm))}</td>
                                    <td className="num">{metric(u, (p) => formatMoney(p.last12))}</td>
                                    <td className="num">{metric(u, (p) => `${p.occupancyCostRatio}%`)}</td>
                                    <td className="num">{metric(u, (p) => `${p.conversionRate}%`)}</td>
                                    <td className="num">
                                        <span className={`mm-risk-tag is-${(u.riskLabel || '').toLowerCase()}`}>{u.riskLabel}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <p className="mm-note">
                The highlighted row is the subject unit. Peers are matched on category and
                gross leasable area, then ranked by size proximity.
            </p>
        </Shell>
    );
}

/** Lease abstract — the summary a leasing team would read before the full deed. */
export function LeaseDocModal({ unit, onClose }) {
    if (!unit || !unit.lease) return null;
    const l = unit.lease;

    return (
        <Shell title={`Lease abstract — ${l.documentRef}`} icon={<FileText size={16} />} onClose={onClose}>
            <div className="mm-doc">
                <div className="mm-doc-banner">
                    Demonstration document. Generated from the unit record — not a legal instrument.
                </div>

                <h4>Parties</h4>
                <div className="mm-kv-grid">
                    <div className="mm-kv"><span className="mm-kv-label">Landlord</span><span className="mm-kv-value">EGoal Mall Management LLC</span></div>
                    <div className="mm-kv"><span className="mm-kv-label">Tenant</span><span className="mm-kv-value">{unit.brand || unit.name}</span></div>
                    <div className="mm-kv"><span className="mm-kv-label">Premises</span><span className="mm-kv-value">{unit.code}, {unit.floorName}</span></div>
                    <div className="mm-kv"><span className="mm-kv-label">Permitted use</span><span className="mm-kv-value">{unit.category}</span></div>
                </div>

                <h4>Term</h4>
                <div className="mm-kv-grid">
                    <div className="mm-kv"><span className="mm-kv-label">Commencement</span><span className="mm-kv-value">{formatDateLong(l.startDate)}</span></div>
                    <div className="mm-kv"><span className="mm-kv-label">Expiry</span><span className="mm-kv-value">{formatDateLong(l.endDate)}</span></div>
                    <div className="mm-kv"><span className="mm-kv-label">Duration</span><span className="mm-kv-value">{l.termYears} years</span></div>
                    <div className="mm-kv"><span className="mm-kv-label">Unexpired</span><span className="mm-kv-value">{l.remainingMonths} months</span></div>
                </div>

                <h4>Consideration</h4>
                <div className="mm-kv-grid">
                    <div className="mm-kv"><span className="mm-kv-label">Structure</span><span className="mm-kv-value">{l.type}</span></div>
                    <div className="mm-kv"><span className="mm-kv-label">Annual base rent</span><span className="mm-kv-value">{formatMoneyExact(l.baseRentAnnual)}</span></div>
                    <div className="mm-kv"><span className="mm-kv-label">Service charge</span><span className="mm-kv-value">{formatMoneyExact(l.serviceChargeAnnual)}</span></div>
                    <div className="mm-kv"><span className="mm-kv-label">Deposit</span><span className="mm-kv-value">{formatMoneyExact(l.deposit)}</span></div>
                </div>

                <h4>Covenants &amp; options</h4>
                <ol className="mm-doc-clauses">
                    {l.clauses.map((c) => <li key={c}>{c}</li>)}
                </ol>

                <h4>Administration</h4>
                <div className="mm-kv-grid">
                    <div className="mm-kv"><span className="mm-kv-label">Leasing manager</span><span className="mm-kv-value">{l.manager.name}</span></div>
                    <div className="mm-kv"><span className="mm-kv-label">Contact</span><span className="mm-kv-value">{l.manager.email}</span></div>
                </div>

                <button type="button" className="mm-btn is-primary mm-doc-print" onClick={() => window.print()}>
                    <Printer size={15} /> Print / save as PDF
                </button>
            </div>
        </Shell>
    );
}
